import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { User } from 'lucide-react';

const ProfileContainer = styled.div`
  width: 100%;
  padding: 4px 12px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: ${({ $isCollapsed }) => $isCollapsed ? 'center' : 'flex-start'};
  width: 100%;
  height: 40px;
  padding: ${({ $isCollapsed }) => $isCollapsed ? '6px' : '6px 8px'};
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.dark.elevated};
  }
`;

const AvatarWrapper = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px solid ${({ theme }) => theme.colors.dark.border};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: white;
  }
`;

const OnlineIndicator = styled.div`
  width: 8px;
  height: 8px;
  background: ${({ theme }) => theme.colors.status.online};
  border-radius: 50%;
  position: absolute;
  bottom: -2px;
  right: -2px;
  border: 2px solid ${({ theme }) => theme.colors.dark.surface};
`;

const ProfileInfoWrapper = styled.div`
  margin-left: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
  flex: 1;
  min-width: 0;
  display: ${({ $isCollapsed }) => $isCollapsed ? 'none' : 'block'};
`;

const ProfileInfo = styled(motion.div)`
  text-align: left;
`;

const UserName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

const UserEmail = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  margin-top: 1px;
`;

const UserProfile = ({ user, isCollapsed, onClick }) => {
  if (!user) return null;

  // Get the display name and avatar
  const displayName = user.displayName || user.name || 'User';
  const avatarUrl = user.photoURL || user.avatar_url;
  const email = user.email || '';

  return (
    <ProfileContainer>
      <ProfileButton onClick={onClick} $isCollapsed={isCollapsed}>
        <AvatarWrapper>
          <Avatar>
            {avatarUrl ? (
              <img 
                src={avatarUrl} 
                alt={displayName}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <User style={{ display: avatarUrl ? 'none' : 'flex' }} />
          </Avatar>
          <OnlineIndicator />
        </AvatarWrapper>
        
        <AnimatePresence>
          {!isCollapsed && (
            <ProfileInfoWrapper $isCollapsed={isCollapsed}>
              <ProfileInfo
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                <UserName>{displayName}</UserName>
                <UserEmail>{email}</UserEmail>
              </ProfileInfo>
            </ProfileInfoWrapper>
          )}
        </AnimatePresence>
      </ProfileButton>
    </ProfileContainer>
  );
};

export default UserProfile;
